import {
  Box,
  Drawer,
  DrawerContent,
  DrawerOverlay,
  Flex,
  IconButton,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react';
import { CrossOutline, MenuOutline } from '@aslanpay/portal-common-lib';
import { ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SIDEBAR_WIDTH = '252px';

interface SidebarShellProps {
  /** Rail and drawer share a ground, so the panel reads as the rail sliding in. */
  bg: string;
  /** Colour for the menu and close buttons, which sit on that ground. */
  controlColor: string;
  /** Sits to the left of the menu button on small screens — a logo, a badge, actions. */
  mobileHeader?: ReactNode;
  drawerWidth?: string;
  /** Pinned to the bottom of the rail and the drawer, below the content — e.g. "Powered by". */
  footer?: ReactNode;
  'data-testid': string;
  /**
   * The rail's content. `onNavigate` closes the drawer on small screens and does nothing
   * on the desktop rail, so a row can call it without caring which it is in. `isDrawer`
   * is for the rare case that has to differ — a pop-up panel would be clipped in there.
   */
  children: (options: { onNavigate: () => void; isDrawer: boolean }) => ReactNode;
}

/**
 * The frame both portals' sidebars sit in: a sticky rail on desktop, and a sticky header
 * with a slide-in drawer below it. Each portal supplies its own colours and contents.
 */
function SidebarShell({
  bg,
  controlColor,
  mobileHeader = undefined,
  drawerWidth = '280px',
  footer = undefined,
  'data-testid': testId,
  children,
}: SidebarShellProps) {
  const isDesktop = useBreakpointValue({ base: false, lg: true }) ?? false;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { pathname } = useLocation();

  // Following a link inside the drawer should leave it behind.
  useEffect(() => {
    onClose();
  }, [pathname, onClose]);

  if (isDesktop) {
    return (
      <Box
        as="aside"
        data-testid={testId}
        w={SIDEBAR_WIDTH}
        minW={SIDEBAR_WIDTH}
        h="100vh"
        position="sticky"
        top={0}
        bg={bg}
        flexShrink={0}
        display="flex"
        flexDirection="column"
      >
        <Box flex="1" minH={0}>
          {children({ onNavigate: () => {}, isDrawer: false })}
        </Box>
        {footer && <Box flexShrink={0}>{footer}</Box>}
      </Box>
    );
  }

  return (
    <>
      <Flex
        as="header"
        data-testid={`${testId}-mobile-header`}
        align="center"
        justify="space-between"
        gap="12px"
        h="64px"
        px="16px"
        bg={bg}
        position="sticky"
        top={0}
        zIndex="sticky"
      >
        <Flex align="center" gap="12px" minW={0}>
          {mobileHeader}
        </Flex>
        <IconButton
          aria-label="Open menu"
          flexShrink={0}
          variant="ghost"
          color={controlColor}
          icon={<MenuOutline boxSize="24px" />}
          boxSize="44px"
          _hover={{ bg: 'brand.whiteAlpha10' }}
          onClick={onOpen}
          data-testid={`${testId}-toggle`}
        />
      </Flex>

      <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent maxW={drawerWidth} bg={bg}>
          <IconButton
            aria-label="Close menu"
            variant="ghost"
            color={controlColor}
            icon={<CrossOutline boxSize="24px" />}
            boxSize="44px"
            position="absolute"
            top="12px"
            right="12px"
            zIndex={1}
            _hover={{ bg: 'brand.whiteAlpha10' }}
            onClick={onClose}
          />
          <Box flex="1" minH={0}>
            {children({ onNavigate: onClose, isDrawer: true })}
          </Box>
          {footer && <Box flexShrink={0}>{footer}</Box>}
        </DrawerContent>
      </Drawer>
    </>
  );
}

export default SidebarShell;
